/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * TanStack Query hooks for Incidents
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  keepPreviousData,
} from "@tanstack/react-query";
import { authenticatedApi, unwrapResponse } from "@/api/authenticatedClient";
import type {
  ApiResponse,
  PaginatedData,
  Incident,
  IncidentComment,
  IncidentListItem,
  IncidentHistoryItem,
  IncidentFilters,
  IncidentStats,
  IncidentTrends,
  IncidentDailyTrends,
  IncidentEnvironments,
  IncidentAnalyticsResponse,
  CreateIncidentInput,
  UpdateIncidentInput,
} from "@/lib/types";

// ============================================================================
// Query Keys
// ============================================================================

export const incidentKeys = {
  all: ["incidents"] as const,
  lists: () => [...incidentKeys.all, "list"] as const,
  list: (filters: IncidentFilters) => [...incidentKeys.lists(), filters] as const,
  details: () => [...incidentKeys.all, "detail"] as const,
  detail: (id: string) => [...incidentKeys.details(), id] as const,
  comments: (incidentId: string) => [...incidentKeys.detail(incidentId), "comments"] as const,
  // Under the detail subtree so any mutation that invalidates detail(id) — status change,
  // assignment, comment — also refreshes the Activity/History tab.
  history: (incidentId: string) => [...incidentKeys.detail(incidentId), "history"] as const,
  stats: () => [...incidentKeys.all, "stats"] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch paginated list of incidents.
 * Backend returns { items, total, page, pageSize, totalPages };
 * we transform to the standard PaginatedData shape.
 */
export function useIncidents(filters: IncidentFilters = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: incidentKeys.list(filters),
    enabled: options?.enabled !== false,
    queryFn: async (): Promise<PaginatedData<IncidentListItem>> => {
      const response = await authenticatedApi.get<
        ApiResponse<{
          items: IncidentListItem[];
          total: number;
          page: number;
          pageSize: number;
          totalPages: number;
        }>
      >("/incidents/", {
        params: {
          search: filters.search || undefined,
          status: filters.status || undefined,
          severity: filters.severity || undefined,
          priority: filters.priority || undefined,
          assignedTo: filters.assignedTo || undefined,
          externalSystem: filters.externalSystem || undefined,
          syncStatus: filters.syncStatus || undefined,
          environment: filters.environment || undefined,
          isSlaBreach: filters.isSlaBreach ?? undefined,
          escalationLevel: filters.escalationLevel || undefined,
          measureId: filters.measureId || undefined,
          page: filters.page || undefined,
          pageSize: filters.pageSize || undefined,
          sortField: filters.sortField || undefined,
          sortDirection: filters.sortDirection || undefined,
          date_from: filters.dateFrom || undefined,
        },
      });
      const raw = unwrapResponse(response);
      return {
        data: raw.items,
        pagination: {
          total: raw.total,
          page: raw.page,
          pageSize: raw.pageSize,
          totalPages: raw.totalPages,
        },
      };
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch infinite list of incidents (for infinite scroll)
 */
export function useInfiniteIncidents(filters: Omit<IncidentFilters, "page">) {
  return useInfiniteQuery({
    queryKey: [...incidentKeys.lists(), "infinite", filters],
    queryFn: async ({ pageParam }): Promise<PaginatedData<IncidentListItem>> => {
      const response = await authenticatedApi.get<
        ApiResponse<{
          items: IncidentListItem[];
          total: number;
          page: number;
          pageSize: number;
          totalPages: number;
        }>
      >("/incidents/", {
        params: {
          search: filters.search || undefined,
          status: filters.status || undefined,
          severity: filters.severity || undefined,
          priority: filters.priority || undefined,
          assignedTo: filters.assignedTo || undefined,
          externalSystem: filters.externalSystem || undefined,
          syncStatus: filters.syncStatus || undefined,
          environment: filters.environment || undefined,
          isSlaBreach: filters.isSlaBreach ?? undefined,
          escalationLevel: filters.escalationLevel || undefined,
          page: pageParam,
          pageSize: filters.pageSize || 50,
          sortField: filters.sortField || undefined,
          sortDirection: filters.sortDirection || undefined,
        },
      });
      const raw = unwrapResponse(response);
      return {
        data: raw.items,
        pagination: {
          total: raw.total,
          page: raw.page,
          pageSize: raw.pageSize,
          totalPages: raw.totalPages,
        },
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch incident detail by ID
 */
export function useIncident(id: string | undefined) {
  return useQuery({
    queryKey: incidentKeys.detail(id ?? ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<Incident>>(`/incidents/${id ?? ""}`);
      return unwrapResponse(response);
    },
    enabled: !!id,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch incidents linked to a submission ticket
 */
export function useSubmissionIncidents(submissionId: string | undefined) {
  return useQuery({
    queryKey: [...incidentKeys.lists(), "submission", submissionId],
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<IncidentListItem[]>>(
        `/submissions/${submissionId ?? ""}/incidents`
      );
      return unwrapResponse(response);
    },
    enabled: !!submissionId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch comments for an incident
 */
export function useIncidentComments(incidentId: string | undefined) {
  return useQuery({
    queryKey: incidentKeys.comments(incidentId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<
        ApiResponse<{ items: IncidentComment[]; total: number }>
      >(`/incidents/${incidentId ?? ""}/comments`);
      const data = unwrapResponse(response);
      // Backend returns { items: [...], total: N } - extract items array
      return data.items;
    },
    enabled: !!incidentId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Fetch activity history for an incident
 */
export function useIncidentHistory(incidentId: string | undefined) {
  return useQuery({
    queryKey: incidentKeys.history(incidentId || ""),
    queryFn: async () => {
      const response = await authenticatedApi.get<
        ApiResponse<{ items: IncidentHistoryItem[]; total: number }>
      >(`/incidents/${incidentId ?? ""}/history`);
      const data = unwrapResponse(response);
      // Backend returns { items: [...], total: N } - extract items array
      return data.items;
    },
    enabled: !!incidentId,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch aggregated incident statistics.
 * Pass dateFrom/dateTo as ISO strings to filter by created_at range.
 * When date params are supplied the backend skips the materialized view and
 * queries the base table, so results are always accurate for the period.
 */
export function useIncidentStats(environment?: string, dateFrom?: string, dateTo?: string) {
  return useQuery({
    queryKey: [...incidentKeys.stats(), environment, dateFrom, dateTo],
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<IncidentStats>>(
        "/incidents/stats/summary",
        {
          params: {
            environment: environment || undefined,
            date_from: dateFrom,
            date_to: dateTo,
          },
        }
      );
      return unwrapResponse(response);
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch distinct non-null environment values that exist in the incidents table.
 * Used to drive the environment filter dropdown on the analytics page.
 */
export function useIncidentEnvironments() {
  return useQuery({
    queryKey: [...incidentKeys.all, "environments"],
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<IncidentEnvironments>>(
        "/incidents/stats/environments"
      );
      return unwrapResponse(response);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
}

/**
 * Fetch daily incident trends (opened vs resolved).
 * Pass dateFrom (ISO string) to align with the period filter; falls back to `days`.
 */
export function useIncidentDailyTrends(dateFrom?: string) {
  return useQuery({
    queryKey: [...incidentKeys.all, "daily-trends", dateFrom ?? "default"],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (dateFrom) {
        params.date_from = dateFrom;
      } else {
        params.days = 30;
      }
      const response = await authenticatedApi.get<ApiResponse<IncidentDailyTrends>>(
        "/incidents/stats/daily-trends",
        { params }
      );
      return unwrapResponse(response);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch monthly incident trends (opened vs resolved) for the last N months.
 */
export function useIncidentTrends(months: number = 12) {
  return useQuery({
    queryKey: [...incidentKeys.all, "trends", months],
    queryFn: async () => {
      const response = await authenticatedApi.get<ApiResponse<IncidentTrends>>(
        "/incidents/stats/trends",
        { params: { months } }
      );
      return unwrapResponse(response);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch all incident analytics panels in a single call.
 * Both params are ISO date strings (YYYY-MM-DD).
 */
export function useIncidentAnalytics(startDate: string | undefined, endDate: string | undefined) {
  return useQuery({
    queryKey: [...incidentKeys.all, "analytics", startDate ?? "all", endDate ?? "all"],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const response = await authenticatedApi.get<ApiResponse<IncidentAnalyticsResponse>>(
        "/incidents/stats/analytics",
        { params }
      );
      return unwrapResponse(response);
    },
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Create a new incident
 */
export function useCreateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateIncidentInput) => {
      const response = await authenticatedApi.post<ApiResponse<Incident>>("/incidents/", input);
      return unwrapResponse(response);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.stats() });
      void queryClient.invalidateQueries({ queryKey: [...incidentKeys.all, "analytics"] });
    },
  });
}

/**
 * Update an incident
 */
export function useUpdateIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateIncidentInput }) => {
      const response = await authenticatedApi.patch<ApiResponse<Incident>>(
        `/incidents/${id}`,
        data
      );
      return unwrapResponse(response);
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.stats() });
      void queryClient.invalidateQueries({ queryKey: [...incidentKeys.all, "analytics"] });
    },
  });
}

/**
 * Assign an incident to a user (or unassign with null)
 */
export function useAssignIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string | null }) => {
      // Backend expects snake_case: { user_id }
      const response = await authenticatedApi.post<ApiResponse<Incident>>(
        `/incidents/${id}/assign`,
        { user_id: userId }
      );
      return unwrapResponse(response);
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.stats() });
      void queryClient.invalidateQueries({ queryKey: [...incidentKeys.all, "analytics"] });
    },
  });
}

/**
 * Change incident status
 */
export function useChangeIncidentStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      status,
      resolutionNotes,
    }: {
      id: string;
      status: string;
      resolutionNotes?: string;
    }) => {
      // Backend expects snake_case: { status, resolution_notes }
      const response = await authenticatedApi.post<ApiResponse<Incident>>(
        `/incidents/${id}/status`,
        { status, resolution_notes: resolutionNotes }
      );
      return unwrapResponse(response);
    },
    onSuccess: (_, { id }) => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.stats() });
      void queryClient.invalidateQueries({ queryKey: [...incidentKeys.all, "analytics"] });
    },
  });
}

/**
 * Add a comment to an incident
 */
export function useAddIncidentComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      incidentId,
      content,
      isInternal = false,
    }: {
      incidentId: string;
      content: string;
      isInternal?: boolean;
    }) => {
      const response = await authenticatedApi.post<ApiResponse<IncidentComment>>(
        `/incidents/${incidentId}/comments`,
        { content, is_internal: isInternal }
      );
      return unwrapResponse(response);
    },
    onSuccess: (_, { incidentId }) => {
      // detail(id) covers the comments list and the Activity/History tab (both nested under it).
      void queryClient.invalidateQueries({ queryKey: incidentKeys.detail(incidentId) });
    },
  });
}

/**
 * Delete an incident
 */
export function useDeleteIncident() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await authenticatedApi.delete(`/incidents/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: incidentKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: incidentKeys.stats() });
      // Keep incident trend/analytics panels from overcounting after a delete (parity with the
      // create/update/status/assign mutations, which already invalidate analytics).
      void queryClient.invalidateQueries({ queryKey: [...incidentKeys.all, "analytics"] });
    },
  });
}

// ============================================================================
// Prefetch Helpers
// ============================================================================

/**
 * Prefetch incident detail on hover
 */
export function usePrefetchIncident() {
  const queryClient = useQueryClient();

  return (id: string) => {
    void queryClient
      .query({
        queryKey: incidentKeys.detail(id),
        queryFn: async () => {
          const response = await authenticatedApi.get<ApiResponse<Incident>>(`/incidents/${id}`);
          return unwrapResponse(response);
        },
        staleTime: 60 * 1000,
        gcTime: 10 * 60 * 1000,
      })
      // prefetchQuery swallowed failures; query() rejects. A hover prefetch must stay
      // silent -- if it fails, the real fetch on navigation surfaces the error properly.
      //
      // Use query(), not fetchQuery(): fetchQuery is deprecated in @tanstack/react-query 5.102
      // ("Use queryClient.query(options) instead"), and eslint's no-deprecated fails the build on
      // it. query() looks absent only when node_modules is stale on 5.101 -- run `npm ci` before
      // concluding it does not exist.
      .catch(() => {});
  };
}
